/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { action, computed, observable } from 'mobx';

/**
 * A blueprint for an object that keeps track of an
 * operation. The operation status is tracked by the
 * state observable. If it errors, the error is tracked
 * in the error observable. The caller of the store might
 * be interested in some additional context data. This
 * is stored in the context object.
 */
// eslint-disable-next-line
export class ProgressTrackerStore<T = {}> {
    @observable public state: 'notStarted' | 'running' | 'completed' | 'failed' = 'notStarted';

    @observable public error: Error | undefined;

    @observable public context: T;

    @computed
    public get isNotStarted() {
        return this.state === 'notStarted';
    }

    @computed
    public get isRunning() {
        return this.state === 'running';
    }

    @computed
    public get isCompleted() {
        return this.state === 'completed';
    }

    @computed
    public get isFailed() {
        return this.state === 'failed';
    }

    @computed
    public get isRunningOrCompleted() {
        return this.isRunning || this.isCompleted;
    }

    public setState(state: ProgressTrackerStore<T>['state']): void;
    public setState(state: 'failed', error?: Error): void;
    @action
    public setState(state: ProgressTrackerStore<T>['state'], error?: Error) {
        this.state = state;

        if (error) {
            this.error = error;
        }
    }

    /**
     * Sets the tracker private context with the given value.
     *
     * @param data The current context of the tracker.
     */
    @action
    public setContext(data: T) {
        this.context = data;
    }

    @action
    public reset() {
        this.state = 'notStarted';
        this.error = undefined;
        this.context = undefined;
    }
}
